import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { toast } from './toast'

declare module 'axios' {
  export interface AxiosRequestConfig {
    silentError?: boolean
  }

  export interface InternalAxiosRequestConfig {
    silentError?: boolean
  }
}

const isMobileRoute = () => {
  const hash = window.location.hash || ''
  return hash === '#/m' || hash.startsWith('#/m/')
}

const getTokenStorageKey = () => (isMobileRoute() ? 'worker_token' : 'token')
const getLoginPath = () => (isMobileRoute() ? '#/m/login' : '#/login')

const clearAuthStorage = () => {
  if (isMobileRoute()) {
    localStorage.removeItem('worker_token')
    localStorage.removeItem('worker_session')
    return
  }
  localStorage.removeItem('token')
  localStorage.removeItem('isLoggedIn')
}

const parseSystemError = (msg: string, defaultMsg: string): string => {
  const text = msg || ''
  if (/redis|lettuce/i.test(text)) {
    return '【环境提示】系统缓存服务（Redis）连接失败，请检查 Redis 进程或网络映射。'
  }
  if (/connection refused/i.test(text)) {
    return '【环境提示】后端服务无响应（Connection Refused），请确认后端进程已启动。'
  }
  return text || defaultMsg
}

const shouldSilenceErrorToast = (config?: AxiosRequestConfig) => Boolean(config?.silentError) || isMobileRoute()

export interface ApiResult<T = any> {
  code: number
  message: string
  data: T
}

export interface PageResult<T = any> {
  records: T[]
  total: number
  pageNum: number
  pageSize: number
}

const service: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
})

service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(getTokenStorageKey())
    if (token && config.headers) {
      config.headers.Authorization = token
    }
    return config
  },
  (error) => Promise.reject(error),
)

service.interceptors.response.use(
  (response: AxiosResponse<ApiResult>) => {
    if (response.config.responseType === 'blob' || response.config.responseType === 'arraybuffer') {
      return response.data as any
    }

    const res = response.data

    if (res.code !== 200) {
      if (res.code === 401) {
        clearAuthStorage()
        if (window.location.hash !== getLoginPath()) {
          if (!shouldSilenceErrorToast(response.config)) {
            toast.error(res.message || '登录已过期，请重新登录')
          }
          setTimeout(() => {
            window.location.href = getLoginPath()
          }, 300)
        }
      } else if (!shouldSilenceErrorToast(response.config)) {
        toast.error(parseSystemError(res.message, '业务请求失败'))
      }
      return Promise.reject(new Error(res.message || '请求失败'))
    }

    return res as any
  },
  (error) => {
    const status = error.response?.status
    const silentError = shouldSilenceErrorToast(error.config)

    if (status === 401) {
      clearAuthStorage()
      if (window.location.hash !== getLoginPath()) {
        if (!silentError) {
          toast.error('登录已过期，请重新登录')
        }
        setTimeout(() => {
          window.location.href = getLoginPath()
        }, 300)
      }
    } else if (!silentError) {
      if (status === 403) {
        toast.error('没有权限访问该资源')
      } else if (status === 404) {
        toast.error('请求的接口不存在')
      } else if (status === 500) {
        const msg = error.response?.data?.message || '服务器内部异常'
        toast.error(parseSystemError(msg, '服务器内部异常'))
      } else {
        toast.error(parseSystemError(error.message, '网络异常，请检查网络连接'))
      }
    }

    return Promise.reject(error)
  },
)

export default service
